import {
  FC,
  RefObject,
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useFormik, FormikHelpers, FormikProvider } from 'formik';
import { useSnackbar } from 'notistack';

import { useRequest, useLazyRequest, useAppSelector } from '~hooks/index';
import { selectAuth } from '~app/store/slices/auth';
import { assignDriver as assignDriverInRoutes } from '~services/route';
import { RouteAssignDriverResponse } from '~services/route/types';
import {
  getDriverCompanyAvailablesByRouteId,
  getDriverCompanyAvailables,
} from '~services/driver';
import {
  DriverCompanyBase,
  DriverCompanyAvailablesByRouteHead,
} from '~services/driver/types';
import { DataPaginateResponse } from '~globals/types';

import { CustomAutocompleteRef } from '../CustomAutocomplete/types';
import { DialogAssignDriverProps } from './types';
import {
  FIELDS_NAME,
  initialValues,
  validationSchema,
  findSelectedDriver,
} from './utils';

export type DialogAssignDriverContextTabsId = 'firstRoute' | 'normal';

export interface DialogAssignDriverContextTab {
  activeTab: DialogAssignDriverContextTabsId;
  setActiveTab: (newActiveTab: DialogAssignDriverContextTabsId) => void;
}

export interface DialogAssignDriverContextAvailablesDrivers {
  loading: boolean;
  data: DriverCompanyBase[];
}

export interface DialogAssignDriverContextSelectedDriverAvailable {
  selectedUserLoggedDriver?: DriverCompanyBase;
  selectedLastDriver?: DriverCompanyBase;
}

export interface DialogAssignDriverContextValue
  extends Omit<
    DialogAssignDriverProps,
    | 'routeId'
    | 'showSnackBarSuccess'
    | 'onSuccesSubmit'
    | 'typeFetchList'
    | 'onSubmit'
  > {
  tabs: DialogAssignDriverContextTab;
  availablesDrivers: DialogAssignDriverContextAvailablesDrivers;
  refDriverAutocomplete: RefObject<
    CustomAutocompleteRef<DriverCompanyBase, false>
  >;
  selectedDriverAvailable: DialogAssignDriverContextSelectedDriverAvailable;
  setDefaultValueDriverForm: (
    selectedDriver: DriverCompanyBase | undefined,
  ) => void;
  loadingSubmit: boolean;
  disabledSubmit: boolean;
}

const DialogAssignDriverContext = createContext<DialogAssignDriverContextValue>(
  {
    open: false,
    onClose: () => {},
    lastDriverId: null,
    handleOpenDialogInviteDriver: () => {},
    excludeIsAvailableOptions: false,
    tabs: {
      activeTab: 'firstRoute',
      setActiveTab: () => {},
    },
    refDriverAutocomplete: { current: null },
    availablesDrivers: {
      loading: true,
      data: [],
    },
    selectedDriverAvailable: {
      selectedUserLoggedDriver: undefined,
      selectedLastDriver: undefined,
    },
    setDefaultValueDriverForm: () => {},
    loadingSubmit: false,
    disabledSubmit: false,
  },
);

export const useDialogAssignDriverContext =
  (): DialogAssignDriverContextValue => useContext(DialogAssignDriverContext);

const DialogAssignDriverProvider: FC<DialogAssignDriverProps> = ({
  open,
  onClose,
  routeId,
  lastDriverId,
  showSnackBarSuccess = false,
  onSuccesSubmit,
  handleOpenDialogInviteDriver,
  typeFetchList = 'ByRouteId',
  excludeIsAvailableOptions = false,
  onSubmit: customOnSubmit,
  children,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user: currentLoggedUser } = useAppSelector(selectAuth);

  const [activeTab, setActiveTab] =
    useState<DialogAssignDriverContextTabsId>('firstRoute');

  const [, loadingAssignDriver, , executeAssignDriver] = useLazyRequest({
    request: assignDriverInRoutes,
    withPostSuccess: (response) => {
      if (showSnackBarSuccess) {
        enqueueSnackbar('Se ha asignado el chofer correctamente', {
          variant: 'success',
        });
      }

      const responseData = response.data?.data as RouteAssignDriverResponse;

      onSuccesSubmit?.(responseData);

      onClose();
    },
    withPostFailure: () => {
      enqueueSnackbar('Ha ocurrido un error, intente nuevamente', {
        variant: 'error',
      });
    },
  });

  const onSubmit = useCallback(
    async (
      values: typeof initialValues,
      { setSubmitting }: FormikHelpers<typeof initialValues>,
    ) => {
      const selectedDriver = values[FIELDS_NAME.CURRENT_DRIVER];

      if (selectedDriver) {
        if (customOnSubmit) {
          customOnSubmit(selectedDriver);

          return;
        }

        if (routeId) {
          await executeAssignDriver({
            routeId,
            driverId: selectedDriver.id,
          });
        }
      }

      setSubmitting(false);
    },
    [customOnSubmit, routeId, executeAssignDriver],
  );

  const formikBag = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    validateOnMount: true,
  });

  const { setFieldValue, dirty, isValid, isSubmitting } = formikBag;

  const disabledSubmit = useMemo(() => !dirty || !isValid, [dirty, isValid]);

  const refDriverAutocomplete =
    useRef<CustomAutocompleteRef<DriverCompanyBase, false>>(null);

  const [availablesDrivers, setAvailablesDrivers] = useState<
    DriverCompanyBase[]
  >([]);

  const [selectedUserLoggedDriver, setSelectedUserLoggedDriver] = useState<
    DriverCompanyBase | undefined
  >(undefined);

  const [selectedLastDriver, setSelectedLastDriver] = useState<
    DriverCompanyBase | undefined
  >(undefined);

  const getDefaultSelectedDrivers = useCallback(
    (drivers: DriverCompanyBase[]) => {
      let defaultSelectedUserLoggedDriver: DriverCompanyBase | undefined =
        undefined;

      if (currentLoggedUser?.userId) {
        const currentSelectedUserLoggedDriver = findSelectedDriver(
          drivers,
          currentLoggedUser.userId,
        );

        if (currentSelectedUserLoggedDriver) {
          defaultSelectedUserLoggedDriver = currentSelectedUserLoggedDriver;
        }
      }

      let defaultSelectedLastDriver: DriverCompanyBase | undefined = undefined;

      if (lastDriverId) {
        const currentSelectedLastDriver = findSelectedDriver(
          drivers,
          lastDriverId,
        );

        if (currentSelectedLastDriver) {
          defaultSelectedLastDriver = currentSelectedLastDriver;
        }
      }

      return {
        userLoggedriver: defaultSelectedUserLoggedDriver,
        lastDriver: defaultSelectedLastDriver,
      };
    },
    [currentLoggedUser?.userId, lastDriverId],
  );

  const setDefaultValueDriverForm = useCallback(
    (selectedDriver: DriverCompanyBase | undefined) => {
      if (!selectedDriver) return;

      const validationForSetter = excludeIsAvailableOptions
        ? true
        : Boolean(selectedDriver?.isAvailable);

      if (validationForSetter) {
        setFieldValue(FIELDS_NAME.CURRENT_DRIVER, selectedDriver);
      }
    },
    [excludeIsAvailableOptions, setFieldValue],
  );

  const isValidFetchByRouteId = useMemo(
    () => Boolean(routeId) && typeFetchList === 'ByRouteId',
    [routeId, typeFetchList],
  );

  const [, loadingGetDriverCompanyAvailablesByRouteId] = useRequest({
    request: getDriverCompanyAvailablesByRouteId,
    payload: routeId as string,
    isValidToRequest: isValidFetchByRouteId,
    initialLoading: isValidFetchByRouteId,
    withPostSuccess: (response) => {
      const {
        data: { results: responseAvailablesDriversByRouteId },
        head: { isFirstRoute },
      } = response.data as DataPaginateResponse<
        DriverCompanyBase[],
        null,
        DriverCompanyAvailablesByRouteHead
      >;

      setAvailablesDrivers(responseAvailablesDriversByRouteId);

      const { userLoggedriver, lastDriver } = getDefaultSelectedDrivers(
        responseAvailablesDriversByRouteId,
      );

      setSelectedUserLoggedDriver(userLoggedriver);
      setSelectedLastDriver(lastDriver);

      let defaultSelectedFormDriver = userLoggedriver;
      let defaultTab: DialogAssignDriverContextTabsId = 'firstRoute';

      if (!isFirstRoute) {
        defaultSelectedFormDriver = lastDriver;
        defaultTab = 'normal';
      }

      setDefaultValueDriverForm(defaultSelectedFormDriver);
      setActiveTab(defaultTab);
    },
  });

  const [, loadingGetDriverCompanyAvailables] = useRequest({
    request: getDriverCompanyAvailables,
    payload: null,
    isValidToRequest: !isValidFetchByRouteId,
    initialLoading: !isValidFetchByRouteId,
    withPostSuccess: (response) => {
      const {
        data: { results: responseAvailablesDrivers },
      } = response.data as DataPaginateResponse<DriverCompanyBase[], null>;

      setAvailablesDrivers(responseAvailablesDrivers);

      const { userLoggedriver, lastDriver } = getDefaultSelectedDrivers(
        responseAvailablesDrivers,
      );

      setSelectedUserLoggedDriver(userLoggedriver);
      setSelectedLastDriver(lastDriver);

      setDefaultValueDriverForm(lastDriver);
      setActiveTab('normal');
    },
  });

  const loadingAvailablesDrivers = useMemo(
    () =>
      loadingGetDriverCompanyAvailablesByRouteId ||
      loadingGetDriverCompanyAvailables,
    [
      loadingGetDriverCompanyAvailablesByRouteId,
      loadingGetDriverCompanyAvailables,
    ],
  );

  return (
    <DialogAssignDriverContext.Provider
      value={{
        open,
        onClose,
        handleOpenDialogInviteDriver,
        excludeIsAvailableOptions,
        lastDriverId,
        tabs: {
          activeTab,
          setActiveTab,
        },
        refDriverAutocomplete,
        availablesDrivers: {
          loading: loadingAvailablesDrivers,
          data: availablesDrivers,
        },
        selectedDriverAvailable: {
          selectedUserLoggedDriver,
          selectedLastDriver,
        },
        setDefaultValueDriverForm,
        loadingSubmit: loadingAssignDriver || isSubmitting,
        disabledSubmit,
      }}
    >
      <FormikProvider value={formikBag}>{children}</FormikProvider>
    </DialogAssignDriverContext.Provider>
  );
};

export default DialogAssignDriverProvider;
